import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Avatar,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, Save, PhotoCamera, Lock } from "@mui/icons-material";
import { useAuth } from "../services/auth";
import { blogService } from "../services/api";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://backendapi.vinautochecker.com";

// Helper function to get full avatar URL
const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;

  // If already a full URL, return as is
  if (avatarPath.startsWith("http://") || avatarPath.startsWith("https://")) {
    return avatarPath;
  }

  // Otherwise, construct full URL
  return `${API_BASE_URL}/storage/${avatarPath}`;
};

const Profile = () => {
  const { user, setUser } = useAuth();

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [formData, setFormData] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    avatar: user?.avatar || "",
  });

  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  // Handle profile input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle avatar file selection
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, avatar: file }));
    }
  };

  // Submit profile update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("bio", formData.bio);

      // Only append avatar if it's a new file upload
      if (formData.avatar instanceof File) {
        data.append("avatar", formData.avatar);
      }

      console.log("Submitting profile update...");
      const res = await blogService.updateProfile(data);
      console.log("Response:", res.data);

      // Explicitly check for success
      if (res.data && res.data.success) {
        setMessage({
          type: "success",
          text: res.data.message || "Profile updated successfully",
        });

        // Update user with full avatar URL
        const updatedUser = {
          ...res.data.user,
          avatar: res.data.user.avatar
            ? getAvatarUrl(res.data.user.avatar)
            : null,
        };

        // Update user in auth context
        setUser(updatedUser);

        // Update local formData with the new user data
        setFormData({
          name: updatedUser.name || "",
          bio: updatedUser.bio || "",
          avatar: updatedUser.avatar || "",
        });

        setEditMode(false);
      } else {
        // Handle case where success is false
        throw new Error(res.data.message || "Profile update failed");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      console.error("Error response:", error.response);

      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          error.response?.data?.errors?.[
            Object.keys(error.response?.data?.errors || {})[0]
          ]?.[0] ||
          error.message ||
          "Failed to update profile",
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit password change
  const handlePasswordSubmit = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const res = await blogService.changePassword(passwordData);

      if (res.data && res.data.success) {
        setMessage({ type: "success", text: res.data.message });
        setPasswordDialogOpen(false);
        setPasswordData({
          old_password: "",
          new_password: "",
          new_password_confirmation: "",
        });
      } else {
        throw new Error(res.data.message || "Password change failed");
      }
    } catch (error) {
      console.error("Password change error:", error);

      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          error.response?.data?.errors?.[
            Object.keys(error.response?.data?.errors || {})[0]
          ]?.[0] ||
          error.message ||
          "Failed to change password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        My Profile
      </Typography>

      {message.text && (
        <Alert
          severity={message.type}
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography variant="h6">Personal Information</Typography>
              {!editMode && (
                <Button startIcon={<Edit />} onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              )}
            </Box>

            <form onSubmit={handleProfileSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <Avatar
                      src={
                        formData.avatar instanceof File
                          ? URL.createObjectURL(formData.avatar)
                          : getAvatarUrl(formData.avatar)
                      }
                      sx={{ width: 80, height: 80, mr: 3 }}
                    >
                      {user?.name?.charAt(0)}
                    </Avatar>
                    {editMode && (
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<PhotoCamera />}
                      >
                        Change Photo
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleAvatarUpload}
                        />
                      </Button>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={user?.email || ""}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bio"
                    name="bio"
                    multiline
                    rows={4}
                    value={formData.bio}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    placeholder="Tell us about yourself..."
                  />
                </Grid>

                {editMode && (
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      sx={{ mr: 2 }}
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <Save />
                      }
                    >
                      {loading ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditMode(false);
                        setFormData({
                          name: user?.name || "",
                          bio: user?.bio || "",
                          avatar: user?.avatar || "",
                        });
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>
              <Lock sx={{ verticalAlign: "middle", mr: 1 }} />
              Account Security
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </Paper>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Status
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Email Verification</Typography>
                <Typography
                  variant="body2"
                  color={
                    user?.email_verified_at ? "success.main" : "error.main"
                  }
                >
                  {user?.email_verified_at ? "Verified" : "Pending"}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Account Type</Typography>
                <Typography variant="body2" color="primary">
                  Free Plan
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Name
                </Typography>
                <Typography variant="body1">{user?.name}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Email
                </Typography>
                <Typography variant="body1">{user?.email}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Member Since
                </Typography>
                <Typography variant="body1">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Password Change Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => !loading && setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Old Password"
            type="password"
            fullWidth
            name="old_password"
            value={passwordData.old_password}
            onChange={handlePasswordChange}
            disabled={loading}
            required
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            name="new_password"
            value={passwordData.new_password}
            onChange={handlePasswordChange}
            disabled={loading}
            helperText="Minimum 8 characters"
            required
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type="password"
            fullWidth
            name="new_password_confirmation"
            value={passwordData.new_password_confirmation}
            onChange={handlePasswordChange}
            disabled={loading}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasswordDialogOpen(false);
              setPasswordData({
                old_password: "",
                new_password: "",
                new_password_confirmation: "",
              });
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            disabled={loading}
            variant="contained"
          >
            {loading ? <CircularProgress size={24} /> : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;
